import { useEffect, useRef, useState } from 'react'
import { subscribeInboxEvents } from '@/services/inboxEventBus'

/** Failsafe: a crashed producer must never strand a typing bubble. */
const TYPING_TTL_MS = 90_000

/**
 * Tracks which conversations currently have an AI reply being composed,
 * fed by the ephemeral typing_started/typing_stopped inbox SSE events
 * (spec 2026-07-18). Cleared by typing_stopped, by the reply's own
 * new_message, or by the TTL.
 */
export function useTypingIndicators(): Record<string, boolean> {
  const [typing, setTyping] = useState<Record<string, boolean>>({})
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const timers = timersRef.current

    const clear = (convId: string) => {
      const timer = timers.get(convId)
      if (timer) clearTimeout(timer)
      timers.delete(convId)
      setTyping((prev) => {
        if (!prev[convId]) return prev
        const next = { ...prev }
        delete next[convId]
        return next
      })
    }

    const unsubscribe = subscribeInboxEvents((e) => {
      const convId = e.conversationId
      if (!convId) return
      if (e.type === 'typing_started' && e.actor === 'ai') {
        const existing = timers.get(convId)
        if (existing) clearTimeout(existing)
        timers.set(convId, setTimeout(() => clear(convId), TYPING_TTL_MS))
        setTyping((prev) => (prev[convId] ? prev : { ...prev, [convId]: true }))
      } else if (e.type === 'typing_stopped' || e.type === 'new_message') {
        clear(convId)
      }
    })

    return () => {
      unsubscribe()
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  return typing
}

/** The visitor sends typing_started about every 3 s and never a stop. */
const VISITOR_TYPING_TTL_MS = 8000

/**
 * SA-1381: which widget visitors are typing right now. Set by a visitor
 * typing_started, cleared 8 s after the last one or by the conversation's
 * new_message. typing_stopped is ignored.
 */
export function useVisitorTypingIndicators(): Record<string, boolean> {
  const [typing, setTyping] = useState<Record<string, boolean>>({})
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const timers = timersRef.current

    const clear = (convId: string) => {
      const timer = timers.get(convId)
      if (timer) clearTimeout(timer)
      timers.delete(convId)
      setTyping((prev) => {
        if (!prev[convId]) return prev
        const next = { ...prev }
        delete next[convId]
        return next
      })
    }

    const unsubscribe = subscribeInboxEvents((e) => {
      const convId = e.conversationId
      if (!convId) return
      if (e.type === 'typing_started' && e.actor === 'visitor') {
        const existing = timers.get(convId)
        if (existing) clearTimeout(existing)
        timers.set(convId, setTimeout(() => clear(convId), VISITOR_TYPING_TTL_MS))
        setTyping((prev) => (prev[convId] ? prev : { ...prev, [convId]: true }))
      } else if (e.type === 'new_message') {
        clear(convId)
      }
    })

    return () => {
      unsubscribe()
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  return typing
}
